import Form from 'react-bootstrap/Form';
import { Editor } from '@tinymce/tinymce-react';
import { useCallback, useEffect, useRef } from 'react';
import { outIcon } from '../assets/editor/outIcon';

const FONT_STYLES = {
  Thin: '100',
  ExtraLight: '200',
  Light: '300',
  Regular: '400',
  Medium: '500',
  SemiBold: '600',
  Bold: '700',
  ExtraBold: '800',
  Black: '900',
};

const ONCLICK_EVENTS = {
  'openReferral()': 'openReferral()',
  'openRewards()': 'openRewards()',
  'openAllMyRewards()': 'openAllMyRewards()',
};

function HTMLEditor({
  width,
  preview,
  value,
  language,
  setLanguage,
  onEditorChange,
  onInit,
  onLoadContent,
  allowCustomButtons = false,
}) {
  const editorRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.getContainer()) {
      editorRef.current.getContainer().style.width =
        width - preview.width <= 400 ? '400px' : `${width - preview.width}px`;
      editorRef.current.getContainer().style.height =
        preview.height <= 300 ? '300px' : `${preview.height}px`;
    }
  }, [width, preview]);

  const getButtonPreview = useCallback(
    ({
      htmlStyle = "@import url('https://fonts.googleapis.com/css2?family=Poppins:ital,wght@" +
        '0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;' +
        "1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'); " +
        'body { margin: 0; height: 200px; font-family: Poppins; display:flex; ' +
        'flex-direction:column; align-items:center; justify-content:center; }',
      buttonId,
      buttonType,
      buttonText = '',
      buttonOnClick,
      buttonWidth,
      buttonHeight,
    }) => {
      let buttonGeneralStyle = '';

      if (buttonType === 'type_1') {
        buttonGeneralStyle =
          'background-color:#350569; border-color: #350569; border-radius: 10px;' +
          "font-size:15px; font-weight:600; font-family: 'Poppins'; " +
          'color:#FFFFFF; text-align:center;';
      }

      if (buttonType === 'type_2') {
        buttonGeneralStyle =
          'background-color:transparent; border:none; ' +
          "font-size:15px; font-weight:500; font-family:'Poppins'; " +
          'color:#350569; text-align:center; text-decoration:underline;';
      }

      return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0" />
      <style>
        ${htmlStyle}
      </style>
    </head>
    <body>
      <button
        style="${buttonGeneralStyle} width:${buttonWidth}px; height:${buttonHeight}px;" 
        onclick="${buttonOnClick}"
        type="button"
        data-button-type="${buttonType}"
        data-button-id="${buttonId}"
      >${buttonText}</button>
    </body>
    </html>
  `;
    },
    [],
  );

  const selectedButtonRef = useRef(null);

  const getButtonsDialog = useCallback(
    (editor, update) => ({
      text: update ? 'Update Button' : 'Insert Button',
      icon: 'customButtonsIcon',
      onAction: () => {
        const target = selectedButtonRef.current;
        const isEditing = target?.hasAttribute('data-button-id');

        const buttonId = isEditing ? target.getAttribute('data-button-id') : crypto.randomUUID();
        const initialData = isEditing
          ? {
              buttonType: target.getAttribute('data-button-type'),
              buttonText: target.textContent,
              buttonOnClick: Object.values(ONCLICK_EVENTS).includes(target.getAttribute('onclick'))
                ? target.getAttribute('onclick')
                : 'CUSTOM',
              buttonOnClickCustom: Object.values(ONCLICK_EVENTS).includes(
                target.getAttribute('onclick'),
              )
                ? ''
                : target.getAttribute('onclick'),
              buttonWidth: target.style.width.replace('px', ''),
              buttonHeight: target.style.height.replace('px', ''),
            }
          : {
              buttonType: 'type_1',
              buttonText: 'Invite Now',
              buttonOnClick: 'openReferral()',
              buttonOnClickCustom: '',
              buttonWidth: '150',
              buttonHeight: '30',
            };

        editor.windowManager.open({
          initialData: { ...initialData, iframe: getButtonPreview({ buttonId, ...initialData }) },
          onChange: e => {
            const {
              buttonType,
              buttonText,
              buttonOnClick,
              buttonOnClickCustom,
              buttonWidth,
              buttonHeight,
            } = e.getData();

            if (buttonOnClick === 'CUSTOM') {
              e.setEnabled('buttonOnClickCustom', true);
            } else {
              e.setEnabled('buttonOnClickCustom', false);
            }

            e.setData({
              buttonType,
              buttonText,
              buttonOnClick,
              buttonOnClickCustom: buttonOnClick !== 'CUSTOM' ? '' : buttonOnClickCustom,
              buttonWidth,
              buttonHeight,
              iframe: getButtonPreview({
                buttonId,
                buttonType,
                buttonText,
                buttonOnClick: buttonOnClick === 'CUSTOM' ? buttonOnClickCustom : buttonOnClick,
                buttonWidth,
                buttonHeight,
              }),
            });
          },
          onSubmit: e => {
            const { iframe } = e.getData();

            const parser = new DOMParser();
            const doc = parser.parseFromString(iframe, 'text/html');
            const buttonValue = doc.body.innerHTML || '';

            if (isEditing) {
              editor.dom.setOuterHTML(selectedButtonRef.current, buttonValue);
              editor.dispatch('Change');
            } else {
              editor.execCommand('InsertHTML', false, buttonValue);
            }

            e.close();
          },
          onClose: () => {
            selectedButtonRef.current = null;
          },
          title: isEditing ? 'Update Custom Button' : 'Insert Custom Button',
          body: {
            type: 'panel',
            items: [
              {
                type: 'listbox',
                name: 'buttonType',
                label: 'Button Type',
                enabled: true,
                size: 1,
                items: [
                  { value: 'type_1', text: 'Rounded Rectangular' },
                  { value: 'type_2', text: 'Hyperlink' },
                ],
                context: 'mode:design',
              },
              {
                type: 'input',
                name: 'buttonText',
                inputMode: 'text',
                label: 'Button Text',
                enabled: true,
                maximized: false,
                context: 'mode:design',
              },
              {
                type: 'grid',
                columns: 2,
                items: [
                  {
                    type: 'listbox',
                    name: 'buttonOnClick',
                    label: 'Button onClick Event',
                    enabled: true,
                    size: 1,
                    items: [
                      ...Object.entries(ONCLICK_EVENTS).map(([k, v]) => ({ value: k, text: v })),
                      { value: 'CUSTOM', text: 'CUSTOM' },
                    ],
                    context: 'mode:design',
                  },
                  {
                    type: 'input',
                    name: 'buttonOnClickCustom',
                    inputMode: 'text',
                    label: 'Button onClick Custom Event',
                    enabled: !!(isEditing && initialData.buttonOnClickCustom),
                    maximized: false,
                    context: 'mode:design',
                  },
                ],
              },
              {
                type: 'grid',
                columns: 2,
                items: [
                  {
                    type: 'input',
                    name: 'buttonWidth',
                    inputMode: 'numeric',
                    label: 'Button Width',
                    enabled: true,
                    maximized: false,
                    context: 'mode:design',
                  },
                  {
                    type: 'input',
                    name: 'buttonHeight',
                    inputMode: 'numeric',
                    label: 'Button Height',
                    enabled: true,
                    maximized: false,
                    context: 'mode:design',
                  },
                ],
              },
              {
                type: 'iframe',
                name: 'iframe',
                label: 'Preview',
                sandboxed: true,
                transparent: true,
                streamContent: true,
                border: true,
              },
            ],
          },
          buttons: [
            {
              type: 'cancel',
              name: 'closeButton',
              text: 'Cancel',
            },
            {
              type: 'submit',
              name: 'submitButton',
              text: isEditing ? 'Update' : 'Insert',
              buttonType: 'primary',
            },
          ],
        });
      },
    }),
    [getButtonPreview],
  );

  const deleteButton = useCallback(
    editor => ({
      text: 'Delete Button',
      icon: 'remove',
      onAction: () => {
        editor.dom.remove(selectedButtonRef.current);
        selectedButtonRef.current = null;
        editor.dispatch('Change');
      },
    }),
    [],
  );

  useEffect(() => {
    if (editorRef.current && allowCustomButtons) {
      editorRef.current.ui.registry.addMenuItem(
        'insertButton',
        getButtonsDialog(editorRef.current),
      );
      editorRef.current.ui.registry.addMenuItem(
        'updateButton',
        getButtonsDialog(editorRef.current, true),
      );
      editorRef.current.ui.registry.addMenuItem('deleteButton', deleteButton(editorRef.current));
    }
  }, [allowCustomButtons, getButtonsDialog, deleteButton]);

  return (
    <div className="d-flex">
      <div>
        {language ? (
          <div className="d-flex justify-content-end align-items-end mb-2">
            <Form.Select
              className="bo-filter bo-w-125"
              value={language}
              onChange={e => setLanguage(e.target.value)}
            >
              <option value="en">English</option>
              <option value="es">Spanish</option>
            </Form.Select>
          </div>
        ) : (
          <div className="bo-h-38" />
        )}
        <Editor
          tinymceScriptSrc="/tinymce/tinymce.min.js"
          licenseKey="gpl"
          onInit={(_evt, editor) => {
            editorRef.current = editor;
            if (onInit) {
              onInit();
            }
          }}
          value={value}
          onEditorChange={newValue => {
            if (iframeRef.current?.contentWindow?.document?.body) {
              iframeRef.current.contentWindow.document.body.innerHTML = newValue;
            }
            onEditorChange(newValue);
          }}
          onLoadContent={onLoadContent}
          init={{
            setup: editor => {
              if (allowCustomButtons) {
                editor.ui.registry.addMenuItem('insertButton', getButtonsDialog(editor));
                editor.ui.registry.addMenuItem('updateButton', getButtonsDialog(editor, true));
                editor.ui.registry.addMenuItem('deleteButton', deleteButton(editor));
              }

              editor.ui.registry.addContextMenu('customContextMenu', {
                update: el => {
                  if (el.tagName === 'BUTTON') {
                    selectedButtonRef.current = el;

                    return el.hasAttribute('data-button-id') ? 'updateButton deleteButton' : '';
                  }

                  return 'image link';
                },
              });

              editor.ui.registry.addIcon('customButtonsIcon', outIcon);
            },
            extended_valid_elements:
              'button[onclick|style|type|class|data-button-type|data-button-id]',
            width: width - preview.width <= 400 ? 400 : width - preview.width,
            height: preview.height <= 300 ? 300 : preview.height,
            skin: 'tinymce-5',
            promotion: false,
            content_css: false,
            content_style:
              "@import url('https://fonts.googleapis.com/css2?family=Poppins:" +
              'ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;' +
              "1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'); " +
              'body { font-family: Poppins; }',
            font_family_formats:
              'Poppins=Poppins; ' +
              'Andale Mono=andale mono,times; Arial=arial,helvetica,sans-serif; ' +
              'Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; ' +
              'Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; ' +
              'Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; ' +
              'Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; ' +
              'Terminal=terminal,monaco; Times New Roman=times new roman,times; ' +
              'Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; ' +
              'Webdings=webdings; Wingdings=wingdings,zapf dingbats',
            statusbar: false,
            menubar: 'edit insert format view',
            menu: {
              edit: {
                title: 'Edit',
                items: 'undo redo',
              },
              insert: {
                title: 'Insert',
                items: 'image link media inserttable | charmap emoticons hr | insertButton',
              },
              format: {
                title: 'Format',
                items:
                  'bold italic underline strikethrough superscript subscript codeformat | ' +
                  'styles blocks fontfamily fontsize align lineheight | forecolor backcolor | ' +
                  'language | removeformat',
              },
              view: {
                title: 'View',
                items: 'visualchars visualblocks code',
              },
            },
            contextmenu: 'customContextMenu',
            plugins: [
              'autolink',
              'charmap',
              'code',
              'directionality',
              'emoticons',
              'image',
              'link',
              'lists',
              'advlist',
              'media',
              'nonbreaking',
              'preview',
              'quickbars',
              'searchreplace',
              'table',
              'visualblocks',
              'visualchars',
            ],
            toolbar:
              'fontfamily fontsize | ' +
              'bold italic underline strikethrough forecolor backcolor | ' +
              'align numlist bullist | lineheight outdent indent| table | ' +
              'ltr rtl',
            toolbar_mode: 'wrap',
            quickbars_selection_toolbar: false,
            quickbars_insert_toolbar: false,
            font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 24pt 36pt 48pt',
            style_formats: [
              {
                title: 'Font Styles',
                items: [
                  ...Object.entries(FONT_STYLES).map(([key, val]) => ({
                    title: key,
                    inline: 'span',
                    styles: { 'font-weight': val },
                  })),
                  ...Object.entries(FONT_STYLES).map(([key, val]) => ({
                    title: `${key} Italic`,
                    inline: 'span',
                    styles: { 'font-weight': val, 'font-style': 'italic' },
                  })),
                ],
              },
              {
                title: 'Headers',
                items: [
                  { title: 'Heading 1', block: 'h1' },
                  { title: 'Heading 2', block: 'h2' },
                  { title: 'Heading 3', block: 'h3' },
                  { title: 'Heading 4', block: 'h4' },
                  { title: 'Heading 5', block: 'h5' },
                  { title: 'Heading 6', block: 'h6' },
                ],
              },
              {
                title: 'Blocks',
                items: [
                  { title: 'Paragraph', block: 'p' },
                  { title: 'Blockquote', block: 'blockquote' },
                  { title: 'Div', block: 'div' },
                  { title: 'Pre', block: 'pre' },
                ],
              },
            ],
            image_advtab: true,
            automatic_uploads: true,
            file_picker_types: 'image',
            file_picker_callback: cb => {
              const input = document.createElement('input');

              input.setAttribute('type', 'file');
              input.setAttribute('accept', 'image/*');

              input.addEventListener('change', e => {
                const file = e.target.files[0];

                const reader = new FileReader();

                reader.addEventListener('load', () => {
                  const id = `blobid${new Date().getTime()}`;
                  const { blobCache } = editorRef.current.editorUpload;
                  const base64 = reader.result.split(',')[1];
                  const blobInfo = blobCache.create(id, file, base64);

                  blobCache.add(blobInfo);

                  cb(blobInfo.blobUri(), { title: file.name });
                });

                reader.readAsDataURL(file);
              });

              input.click();
            },
          }}
        />
      </div>
    </div>
  );
}

export default HTMLEditor;
